import { useNavigate } from 'react-router-dom';
import {
  Card,
  Text,
  BlockStack,
  Spinner,
  Banner,
  EmptyState,
} from '@shopify/polaris';
import { useSites } from '../hooks/useSites';
import { Layout } from '../components/Layout';
import { webflowApi } from '../lib/api';

export function ReportsSiteSelection() {
  const navigate = useNavigate();
  const { data: sites, isLoading, error } = useSites();

  const allSites = sites || [];

  const handleSiteClick = (siteId: string, hasGA4: boolean, googleConnected: boolean, isAuthorized: boolean) => {
    if (!isAuthorized) {
      window.location.href = webflowApi.getAuthorizeUrl();
      return;
    }

    if (hasGA4 && googleConnected) {
      navigate(`/site/${siteId}/standard-reports`);
    } else if (hasGA4 && !googleConnected) {
      navigate(`/site/${siteId}`);
    } else {
      navigate(`/site/${siteId}`);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <Spinner size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <BlockStack gap="400">
        {/* Page Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <BlockStack gap="100">
            <Text as="h1" variant="headingLg" fontWeight="bold">
              Reports
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Select a site to view analytics reports
            </Text>
          </BlockStack>
        </div>

        {error && (
          <Banner tone="critical">
            Failed to load sites. Please try again.
          </Banner>
        )}

        {allSites.length === 0 ? (
          <Card>
            <EmptyState
              heading="No sites found"
              image="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='160' fill='none'%3E%3Crect x='40' y='30' width='120' height='90' rx='8' fill='%23f0f0f5' stroke='%23c9cccf' stroke-width='2'/%3E%3Cpath d='M60 90 L80 70 L100 80 L120 55 L140 65' stroke='%235c6ac4' stroke-width='3' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3Ccircle cx='80' cy='70' r='3' fill='%235c6ac4'/%3E%3Ccircle cx='100' cy='80' r='3' fill='%235c6ac4'/%3E%3Ccircle cx='120' cy='55' r='3' fill='%235c6ac4'/%3E%3Ccircle cx='140' cy='65' r='3' fill='%235c6ac4'/%3E%3Cline x1='55' y1='100' x2='55' y2='45' stroke='%23c9cccf' stroke-width='1.5'/%3E%3Cline x1='55' y1='100' x2='145' y2='100' stroke='%23c9cccf' stroke-width='1.5'/%3E%3C/svg%3E"
              action={{
                content: 'Go to Dashboard',
                onAction: () => navigate('/dashboard'),
              }}
            >
              <p>
                You don't have any sites yet. Go to the Dashboard to add and set up your sites.
              </p>
            </EmptyState>
          </Card>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '20px',
            }}
          >
            {allSites.map((site) => (
              <div
                key={site.id}
                onClick={() => handleSiteClick(site.id, site.has_ga4_property, site.google_connected, site.is_authorized)}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  border: site.is_authorized ? '1px solid #e1e3e5' : '2px solid #f59e0b',
                  cursor: 'pointer',
                  opacity: site.is_authorized ? 1 : 0.9,
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
                }}
              >
                {/* Site Preview Image */}
                <div
                  style={{
                    height: '160px',
                    backgroundColor: '#f6f6f7',
                    backgroundImage: site.preview_url
                      ? `url(${site.preview_url})`
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                  }}
                >
                  {/* Status Badge */}
                  <div style={{ position: 'absolute', top: '12px', right: '12px' }}>
                    {!site.is_authorized ? (
                      <span
                        style={{
                          backgroundColor: '#fef3c7',
                          color: '#b45309',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        Authorization Required
                      </span>
                    ) : site.has_ga4_property && site.google_connected ? (
                      <span
                        style={{
                          backgroundColor: '#b4fed5',
                          color: '#0d6832',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        Connected
                      </span>
                    ) : site.has_ga4_property && !site.google_connected ? (
                      <span
                        style={{
                          backgroundColor: '#ffd79d',
                          color: '#8a6116',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        Reconnect Required
                      </span>
                    ) : (
                      <span
                        style={{
                          backgroundColor: '#ffe5b4',
                          color: '#8a6a00',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        Setup Required
                      </span>
                    )}
                  </div>
                </div>

                {/* Site Info */}
                <div style={{ padding: '16px' }}>
                  <Text as="h3" variant="headingMd" fontWeight="semibold">
                    {site.display_name}
                  </Text>

                  {site.measurement_id && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      {site.measurement_id}
                    </Text>
                  )}

                  {/* Action Button */}
                  <div style={{ marginTop: '16px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSiteClick(site.id, site.has_ga4_property, site.google_connected, site.is_authorized);
                      }}
                      style={{
                        backgroundColor: !site.is_authorized
                          ? '#f59e0b'
                          : site.has_ga4_property && site.google_connected
                            ? '#5c6ac4'
                            : site.has_ga4_property && !site.google_connected
                              ? '#f4a21b'
                              : '#5c6ac4',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        width: '100%',
                        transition: 'background-color 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = !site.is_authorized
                          ? '#d97706'
                          : site.has_ga4_property && site.google_connected
                            ? '#4959bd'
                            : site.has_ga4_property && !site.google_connected
                              ? '#d68f18'
                              : '#4959bd';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = !site.is_authorized
                          ? '#f59e0b'
                          : site.has_ga4_property && site.google_connected
                            ? '#5c6ac4'
                            : site.has_ga4_property && !site.google_connected
                              ? '#f4a21b'
                              : '#5c6ac4';
                      }}
                    >
                      {!site.is_authorized
                        ? 'Authorize'
                        : site.has_ga4_property && site.google_connected
                          ? 'View Report'
                          : site.has_ga4_property && !site.google_connected
                            ? 'Reconnect'
                            : 'Setup'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </BlockStack>
    </Layout>
  );
}
